/* eslint-env browser */
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import qs from "qs";
import { format, isSameDay, parseISO } from "date-fns";
import { Chart, registerables } from "chart.js";
import { Chart as ChartComponent } from "react-chartjs-2";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import GetAppIcon from "@mui/icons-material/GetApp";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import GeoSelector from "components/General/GeoSelector";
import { API_BASE } from "utils/api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

Chart.register(...registerables);

const Districts = {
  MB: ["5"],
  NB: ["22"],
  NL: ["22"],
  NS: ["22"],
  ON: ["1", "2", "5", "26"],
  QC: ["23", "24", "25", "26"],
  SK: ["2"],
};

const moneyFormat = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  maximumFractionDigits: 0,
});
const numberFormat = new Intl.NumberFormat("en-CA", {
  maximumFractionDigits: 1,
});

const EMPTY_DAY = { items: 0, amount: 0, hourly: [] };

const toNumber = (value) => (Number.isFinite(value) ? value : 0);

/** Sum {items, amount} over `list`, picking the pair with `pick` (identity by default). */
const sumBy = (list, pick = (item) => item) =>
  list.reduce(
    (acc, item) => {
      const entry = pick(item);
      acc.items += toNumber(entry?.items);
      acc.amount += toNumber(entry?.amount);
      return acc;
    },
    { items: 0, amount: 0 },
  );

const isoDate = (date) => format(date, "yyyy-MM-dd");

const metricShort = (metric) => (metric === "amount" ? "$" : "qty");

const formatValue = (value, metric) =>
  metric === "amount"
    ? moneyFormat.format(toNumber(value))
    : numberFormat.format(toNumber(value));

/** "2026-10-30" -> "Fri, Oct 30" for the day block headings. */
const formatDay = (date) => {
  try {
    return format(parseISO(date), "EEE, MMM d");
  } catch (err) {
    return date;
  }
};

/**
 * No store rings a sale before 09:00, so the 08:00-09:00 bucket is always
 * empty and only widens the tables and the chart. Drop it from the labels and
 * from the matching position of every day's `hourly` array.
 */
const dropEmptyFirstHour = (result) => {
  const hours = result?.hours ?? [];
  if (hours[0] !== "08:00-09:00") return result;
  return {
    ...result,
    hours: hours.slice(1),
    stores: (result.stores ?? []).map((store) => ({
      ...store,
      daily: (store.daily ?? []).map((day) => ({
        ...day,
        hourly: (day.hourly ?? []).slice(1),
      })),
    })),
  };
};

/** "08:00-09:00" -> { start: "08", end: "09" } so the header stays narrow. */
const splitHourLabel = (label) => {
  const [start, end] = String(label || "").split("-");
  return {
    start: (start || "").slice(0, 2),
    end: (end || "").slice(0, 2),
  };
};

const peakHour = (hourly, metric) =>
  hourly.reduce(
    (best, entry) =>
      !best || toNumber(entry[metric]) > toNumber(best[metric]) ? entry : best,
    null,
  );

const toTreeItems = (geo) =>
  Object.entries(geo).map(([province, districts]) => ({
    id: province,
    label: province,
    children: districts.map((d) => ({
      id: `${province}-${d}`,
      value: d,
      label: d,
    })),
  }));

/** Group the flat `stores` array into one bucket per district, totalled over the range. */
const groupStoresByDistrict = (stores) => {
  const groups = new Map();
  stores.forEach((store) => {
    const key = store.district_num ?? store.district_name ?? "unknown";
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        districtNum: store.district_num,
        districtName: store.district_name,
        stores: [],
      });
    }
    groups.get(key).stores.push(store);
  });

  return [...groups.values()]
    .map((group) => ({
      ...group,
      ...sumBy(group.stores.flatMap((store) => store.daily ?? [])),
    }))
    .sort((a, b) => (a.districtNum ?? 0) - (b.districtNum ?? 0));
};

/**
 * Reshape the API payload into everything the page renders: districts with
 * range totals, range totals, the shared heatmap scale for both metrics and
 * (for a single selected day) the per-hour chart series. One pass per result.
 */
const summarize = (result) => {
  const data = dropEmptyFirstHour(result);
  const stores = data?.stores ?? [];
  const days = data?.days ?? [];
  const hours = data?.hours ?? [];
  const districts = groupStoresByDistrict(stores);

  const maxByMetric = { items: 0, amount: 0 };
  // One hourly series for the whole range: the chart uses it for a single day,
  // the peak-hour KPI uses it for any range.
  const hourlyTotals = hours.map((label) => ({ label, items: 0, amount: 0 }));

  stores.forEach((store) => {
    (store.daily ?? []).forEach((day) => {
      (day.hourly ?? []).forEach((entry, index) => {
        maxByMetric.items = Math.max(
          maxByMetric.items,
          Math.abs(toNumber(entry.items)),
        );
        maxByMetric.amount = Math.max(
          maxByMetric.amount,
          Math.abs(toNumber(entry.amount)),
        );
        if (hourlyTotals[index]) {
          hourlyTotals[index].items += toNumber(entry.items);
          hourlyTotals[index].amount += toNumber(entry.amount);
        }
      });
    });
  });

  return {
    stores,
    days,
    hours,
    districts,
    totals: sumBy(districts),
    maxByMetric,
    hourlyTotals,
  };
};

const STICKY_FIRST = {
  position: "sticky",
  left: 0,
  zIndex: 3,
  backgroundColor: "background.paper",
  minWidth: 190,
  maxWidth: 190,
  borderRight: "1px solid",
  borderColor: "divider",
};

function KpiCard({ label, value, caption, accent }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="overline" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="h4" sx={{ color: accent, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {caption && (
          <Typography variant="caption" color="textSecondary">
            {caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Both figures are shown in every cell: quantity and amount. The metric toggle
 * only decides which one leads and which one drives the heat.
 */
function DualValue({ value, other, metric, otherMetric, strong }) {
  const empty = !value && !other;
  return (
    <>
      <Box sx={{ fontWeight: strong ? 700 : 600, lineHeight: 1.15 }}>
        {empty ? "–" : formatValue(value, metric)}
      </Box>
      {!empty && (
        <Box sx={{ fontSize: 10, lineHeight: 1.15, color: "text.secondary" }}>
          {formatValue(other, otherMetric)}
        </Box>
      )}
    </>
  );
}

function DistrictTable({ district, days, hours, metric, maxCell }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const otherMetric = metric === "amount" ? "items" : "amount";

  const cellSx = (value) => {
    const amount = Math.abs(toNumber(value));
    if (!amount || !maxCell) return {};
    const ratio = Math.min(1, amount / maxCell);
    return {
      backgroundColor: alpha(primary, 0.05 + ratio * 0.45),
      fontWeight: ratio > 0.6 ? 600 : 400,
    };
  };

  return (
    <>
      {days.map((date, dayIndex) => {
        // The API returns one `daily` entry per day, in `days` order, per store.
        const rows = district.stores.map((store) => ({
          store,
          day: store.daily?.[dayIndex] ?? EMPTY_DAY,
        }));
        const dayTotals = sumBy(rows, (row) => row.day);
        const hourTotals = hours.map((_, hourIndex) =>
          sumBy(rows, (row) => row.day.hourly?.[hourIndex]),
        );

        return (
          <Box
            key={date}
            sx={{
              "& + &": { borderTop: "1px solid", borderColor: "divider" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                flexWrap: "wrap",
                px: 2,
                pt: 1.5,
                pb: 0.5,
              }}
            >
              <Typography variant="subtitle2">{formatDay(date)}</Typography>
              <Typography variant="caption" color="textSecondary">
                Items {numberFormat.format(toNumber(dayTotals.items))} · Sales{" "}
                {moneyFormat.format(toNumber(dayTotals.amount))}
              </Typography>
            </Box>

            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...STICKY_FIRST, zIndex: 4 }}>
                      Store
                      <Typography
                        component="div"
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontSize: 10 }}
                      >
                        {metricShort(metric)} / {metricShort(otherMetric)}
                      </Typography>
                    </TableCell>
                    {hours.map((label) => {
                      const { start, end } = splitHourLabel(label);
                      return (
                        <Tooltip key={label} title={label} arrow>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {start}
                            </Box>
                            <Box
                              component="span"
                              sx={{ color: "text.secondary", fontSize: 11 }}
                            >
                              –{end}
                            </Box>
                          </TableCell>
                        </Tooltip>
                      );
                    })}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Day total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(({ store, day }) => (
                    <TableRow
                      key={store.store_number}
                      hover
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: alpha(primary, 0.02),
                        },
                      }}
                    >
                      <TableCell sx={STICKY_FIRST}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={store.store_name}
                        >
                          <Box
                            component="span"
                            sx={{ color: "text.secondary" }}
                          >
                            #{store.store_number}
                          </Box>{" "}
                          {store.store_name}
                        </Typography>
                      </TableCell>
                      {hours.map((label, hourIndex) => {
                        const entry = day.hourly?.[hourIndex] ?? {};
                        return (
                          <TableCell
                            key={label}
                            align="right"
                            sx={{
                              whiteSpace: "nowrap",
                              py: 0.5,
                              ...cellSx(entry[metric]),
                            }}
                          >
                            <DualValue
                              value={entry[metric]}
                              other={entry[otherMetric]}
                              metric={metric}
                              otherMetric={otherMetric}
                            />
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, whiteSpace: "nowrap", py: 0.5 }}
                      >
                        <DualValue
                          value={day[metric]}
                          other={day[otherMetric]}
                          metric={metric}
                          otherMetric={otherMetric}
                          strong
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: alpha(primary, 0.08) }}>
                    <TableCell sx={{ ...STICKY_FIRST, fontWeight: 700 }}>
                      District total
                    </TableCell>
                    {hourTotals.map((total, index) => (
                      <TableCell
                        key={hours[index] ?? index}
                        align="right"
                        sx={{ fontWeight: 700, whiteSpace: "nowrap", py: 0.5 }}
                      >
                        <DualValue
                          value={total[metric]}
                          other={total[otherMetric]}
                          metric={metric}
                          otherMetric={otherMetric}
                          strong
                        />
                      </TableCell>
                    ))}
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, whiteSpace: "nowrap", py: 0.5 }}
                    >
                      <DualValue
                        value={dayTotals[metric]}
                        other={dayTotals[otherMetric]}
                        metric={metric}
                        otherMetric={otherMetric}
                        strong
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </>
  );
}

export default function HourlySales() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const [geo, setGeo] = useState(null);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [districtsAnchor, setDistrictsAnchor] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState("items");

  useEffect(() => {
    axios
      .get(`${API_BASE}/provinceDistrictsGrouped`)
      .then((res) =>
        setGeo(res.data && Object.keys(res.data).length ? res.data : Districts),
      )
      .catch(() => setGeo(Districts));
  }, []);

  const geographicItems = useMemo(() => (geo ? toTreeItems(geo) : []), [geo]);

  const { stores, days, hours, districts, totals, maxByMetric, hourlyTotals } =
    useMemo(() => summarize(result), [result]);

  const maxCell = maxByMetric[metric];
  const totalPeak = peakHour(hourlyTotals, metric);
  const singleDay = days.length === 1;

  const isToday =
    isSameDay(startDate, new Date()) && isSameDay(endDate, new Date());

  const handleToday = () => {
    const today = new Date();

    setStartDate(today);
    setEndDate(today);

    setResult(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedDistricts.length) {
      setError("Select at least one district.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/hourlySales/hourly_sales/`, {
        params: {
          startDate: isoDate(startDate),
          endDate: isoDate(endDate),
          districtId: selectedDistricts,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      setResult(res.data);
    } catch (err) {
      setResult(null);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not load hourly sales.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!stores.length || !days.length) return;

    const workbook = new ExcelJS.Workbook();

    // Create a separate worksheet for each calendar day
    days.forEach((date, dayIndex) => {
      // Worksheet names cannot exceed 31 characters
      const sheetName = date.slice(0, 31);
      const worksheet = workbook.addWorksheet(sheetName);

      // Ensure gridlines are visible
      worksheet.views = [{ showGridLines: true }];

      // Construct table headers
      const headers = [
        "District #",
        "District Name",
        "Store #",
        "Store Name",
        "Day Items",
        "Day Sales ($)",
        ...hours,
      ];

      const headerRow = worksheet.addRow(headers);

      // Style Header Row (Dark Blue background with bold white text)
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "1F4E78" },
        };
        cell.font = {
          name: "Calibri",
          size: 11,
          bold: true,
          color: { argb: "FFFFFF" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      // Populate data for each store on this specific day
      districts.forEach((district) => {
        district.stores.forEach((store) => {
          const dayData = store.daily?.[dayIndex] ?? EMPTY_DAY;

          // Build values for each hour (uses selected primary metric: amount or items)
          const hourlyValues = hours.map(
            (_, hourIndex) => dayData.hourly?.[hourIndex]?.[metric] ?? 0,
          );

          const rowData = [
            district.districtNum ?? "",
            district.districtName ?? "",
            store.store_number,
            store.store_name,
            dayData.items ?? 0,
            dayData.amount ?? 0,
            ...hourlyValues,
          ];

          const row = worksheet.addRow(rowData);

          // Format Cells (Currency, Number, Borders)
          row.eachCell((cell, colNumber) => {
            cell.font = { name: "Calibri", size: 10 };
            cell.border = {
              top: { style: "thin", color: { argb: "E0E0E0" } },
              left: { style: "thin", color: { argb: "E0E0E0" } },
              bottom: { style: "thin", color: { argb: "E0E0E0" } },
              right: { style: "thin", color: { argb: "E0E0E0" } },
            };

            // Apply number formatting based on column
            if (colNumber === 5) {
              cell.numberFormat = "#,##0"; // Day Items
            } else if (colNumber === 6) {
              cell.numberFormat = "$#,##0.00"; // Day Sales
            } else if (colNumber > 6) {
              cell.numberFormat = metric === "amount" ? "$#,##0.00" : "#,##0";
            }
          });
        });
      });

      // Auto-fit Column Widths cleanly
      worksheet.columns.forEach((column) => {
        let maxLen = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        column.width = Math.max(maxLen + 4, 12);
      });
    });

    // Generate Excel buffer and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `hourly-sales-${result.startDate}_to_${result.endDate}.xlsx`);
  };
  const chartData = {
    labels: hours.map((label) => splitHourLabel(label).start),
    datasets: [
      {
        type: "line",
        label: "Sales ($)",
        data: hours.map((_, index) => toNumber(hourlyTotals[index]?.amount)),
        yAxisID: "y",
        borderColor: primary,
        backgroundColor: primary,
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: false,
      },
      {
        type: "bar",
        label: "Items",
        data: hours.map((_, index) => toNumber(hourlyTotals[index]?.items)),
        yAxisID: "y1",
        backgroundColor: alpha(primary, 0.22),
        hoverBackgroundColor: alpha(primary, 0.4),
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.7,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8 },
      },
      tooltip: {
        callbacks: {
          title: (items) => hours[items[0]?.dataIndex ?? 0] ?? "",
          label: (item) =>
            item.dataset.yAxisID === "y"
              ? `${item.dataset.label}: ${moneyFormat.format(item.parsed.y)}`
              : `${item.dataset.label}: ${numberFormat.format(item.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        position: "left",
        beginAtZero: true,
        title: { display: true, text: "Sales ($)" },
        grid: { color: alpha(theme.palette.text.secondary, 0.12) },
      },
      y1: {
        position: "right",
        beginAtZero: true,
        title: { display: true, text: "Items" },
        grid: { drawOnChartArea: false },
      },
    },
  };

  return (
    <>
      <Helmet>
        <title> Hourly sales | Nutriart</title>
      </Helmet>

      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Hourly sales
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Pick districts and a date range to see sales and items sold per hour,
          broken down store by store.
        </Typography>

        <form noValidate onSubmit={handleSubmit}>
          <Card>
            <CardContent>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={6} md={2}>
                    <DatePicker
                      label="Start date"
                      value={startDate}
                      onChange={(value) => {
                        if (value) {
                          setStartDate(value);
                        }
                      }}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <DatePicker
                      label="End date"
                      value={endDate}
                      onChange={(value) => {
                        if (value) {
                          setEndDate(value);
                        }
                      }}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </Grid>

                  <Grid item xs="auto">
                    <Tooltip title={isToday ? "Already showing today" : ""}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<TodayOutlinedIcon />}
                          onClick={handleToday}
                          disabled={isToday}
                        >
                          Today
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>

                  <Grid item xs="auto">
                    <Button
                      size="small"
                      variant="outlined"
                      color={selectedDistricts.length ? "primary" : "inherit"}
                      startIcon={<PlaceOutlinedIcon />}
                      onClick={(event) =>
                        setDistrictsAnchor(event.currentTarget)
                      }
                    >
                      Districts
                      {selectedDistricts.length > 0 &&
                        ` (${selectedDistricts.length})`}
                    </Button>

                    <Popover
                      open={Boolean(districtsAnchor)}
                      anchorEl={districtsAnchor}
                      onClose={() => setDistrictsAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      slotProps={{ paper: { sx: { p: 1.5, width: 320 } } }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2">Districts</Typography>
                        <Button
                          size="small"
                          disabled={!selectedDistricts.length}
                          onClick={() => setSelectedDistricts([])}
                        >
                          Clear
                        </Button>
                      </Box>

                      {geo ? (
                        <GeoSelector
                          items={geographicItems}
                          selected={selectedDistricts}
                          onChange={setSelectedDistricts}
                          width="100%"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Loading districts...
                        </Typography>
                      )}

                      {selectedDistricts.length > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 1, maxHeight: 96, overflow: "auto" }}
                        >
                          {selectedDistricts.map((district) => (
                            <Chip
                              key={district}
                              label={`District ${district}`}
                              size="small"
                              onDelete={() =>
                                setSelectedDistricts((prev) =>
                                  prev.filter((d) => d !== district),
                                )
                              }
                            />
                          ))}
                        </Stack>
                      )}
                    </Popover>
                  </Grid>

                  <Grid item xs="auto">
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      disabled={loading || !selectedDistricts.length}
                    >
                      Show sales
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </CardContent>
          </Card>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && result && stores.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No sales found for the selected districts and hours. Try widening
            the date range or the hour window.
          </Alert>
        )}

        {!loading && districts.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Sales"
                  value={moneyFormat.format(toNumber(totals.amount))}
                  caption={`${result.startDate} → ${result.endDate}`}
                  accent={primary}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Items sold"
                  value={numberFormat.format(toNumber(totals.items))}
                  caption={`${hours.length} hourly buckets × ${days.length} day${days.length === 1 ? "" : "s"}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Stores"
                  value={numberFormat.format(stores.length)}
                  caption={`${districts.length} district${districts.length === 1 ? "" : "s"}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label={`Peak hour (${metric === "amount" ? "sales" : "items"})`}
                  value={totalPeak?.label?.split("-")[0] || "–"}
                  caption={
                    totalPeak
                      ? formatValue(totalPeak[metric], metric)
                      : "No data"
                  }
                />
              </Grid>
            </Grid>

            {singleDay && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6">
                    {formatDay(days[0])} — hourly trend
                  </Typography>
                  <Box sx={{ height: 280, mt: 2 }}>
                    <ChartComponent
                      type="bar"
                      data={chartData}
                      options={chartOptions}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 3,
                mb: 1,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="h6">
                Stores by district
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  sx={{ ml: 1 }}
                >
                  darker cell ={" "}
                  {metric === "amount" ? "higher sales" : "more items"}
                </Typography>
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="textSecondary">
                  Primary
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={metric}
                  onChange={(event, value) => value && setMetric(value)}
                >
                  <ToggleButton value="amount">Sales ($)</ToggleButton>
                  <ToggleButton value="items">Items</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={handleExport}
                >
                  Download Excel
                </Button>
              </Stack>
            </Box>

            {districts.map((district, index) => {
              return (
                <Accordion
                  key={district.key}
                  defaultExpanded={index < 4}
                  disableGutters
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    "&:before": { display: "none" },
                    mb: 1,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ width: "100%", pr: 1 }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {district.districtNum
                          ? `District ${district.districtNum}`
                          : "District"}
                        {district.districtName && ` — ${district.districtName}`}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${district.stores.length} store${district.stores.length === 1 ? "" : "s"}`}
                      />
                      <Chip
                        size="small"
                        label={`Sales ${moneyFormat.format(district.amount)}`}
                      />
                      <Chip
                        size="small"
                        label={`Items ${numberFormat.format(district.items)}`}
                        variant="outlined"
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <DistrictTable
                      district={district}
                      days={days}
                      hours={hours}
                      metric={metric}
                      maxCell={maxCell}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Divider sx={{ mt: 2, mb: 1 }} />
            <Typography variant="caption" color="textSecondary">
              Hours come from the transaction start time. Void and pay-in
              transactions are excluded; refunds are included as negative
              amounts. 08:00–09:00 is hidden because no store sells before 09:00.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
